import {
  Callout,
  CollapseAllVisibility,
  ICalloutProps,
  IDetailsGroupDividerProps,
  IDetailsRowProps,
  IGroup,
  MessageBarType,
} from '@fluentui/react';
import {
  adjustColumnWidth,
  calloutContentStylesPopover,
  CommonStyles,
  debounce,
  defaultPalette,
  detailsHeaderStylesGroup,
  DetailsList,
  detailsListStylesGroup,
  detailsListStylesGroupCard,
  EmptyMessage,
  flattenNestedRows,
  getGroupHeight,
  getGroupsWithEmptyGroupsOfNextPage,
  getRowWidthByColumns,
  GroupsAndItems,
  IColumnWithDataType,
  IconButton,
  iconButtonStylesNoBG,
  IDetailsListProps,
  IGroupWithDataType,
  Link,
  linkStylesGhost,
  ListRow,
  listRowStylesThin,
  ListRowType,
  Loader,
  loaderStylesSpinnerXLarge,
  onRenderTagColumn,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  type Job,
  type JobProgressOutput,
  type JobProgressOutputProgress,
  JobStatus,
  JobType,
  type ListableJob,
  type ListJobsPageByFeatureSetIdRequest,
  type ListJobsPageByFeatureSetNameRequest,
  type ListJobsPageResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  ContentsTitle,
  defaultPageSize,
  jobStatusColorMap,
  jobStatusNameMap,
  jobTypeNameMap,
} from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import { RetrieveDownloadPanel } from '../RetrieveDownloadPanel/RetrieveDownloadPanel';
import { IJobListClassNames, IJobListStyles, jobListStylesDefault } from './JobList.styles';

enum CalloutType {
  Progress = 'Progress',
  Error = 'Error',
}

export interface JobWithInfo extends ListableJob {
  jobId?: string;
  jobType?: string;
  jobStatus?: string;
  featureSetName?: string;
  creator?: string;
  createdOn?: string;
  derived?: string;
  depthWithHasNextSibling?: boolean[];
  childCount?: number;
  children?: JobWithInfo[];
  errorMessage?: string;
}

const convertListableJobToJobWithInfo = (item: ListableJob): JobWithInfo => {
  const { jobId, jobType, jobStatus, createdBy, createdOn, errorMessage } = item.job || {};
  const { featureSetName } = item.featureSetJobInfo || {};
  return {
    ...item,
    jobId,
    jobType: jobTypeNameMap.get(jobType) || '',
    jobStatus: jobStatusNameMap.get(jobStatus) || '',
    featureSetName,
    creator: createdBy?.name,
    createdOn,
    derived: item.isDerived ? 'Derived' : '',
    errorMessage,
  };
};

const getChildCountAndFlatNestedItems = (items: ListableJob[]) =>
  items.map((item) => {
    const newItems: JobWithInfo[] = flattenNestedRows([item], 'childJobs');
    return { ...newItems[0], childCount: newItems.length - 1, children: newItems.slice(1) };
  });

const getColumns = (
  isCardView: boolean,
  onShowProgress: (target: HTMLDivElement, jobId: string) => void,
  onShowError: (target: HTMLDivElement, job: JobWithInfo) => void,
  onCancel: (jobId?: string) => void,
  onDownload: (job: JobWithInfo) => void,
  groupColumns?: IColumnWithDataType[]
) => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'jobId',
      name: 'Job Id',
      fieldName: 'jobId',
      minWidth: 200,
      data: {
        listCellProps: {
          hasDepth: true,
          iconProps: isCardView && !groupColumns ? { iconName: 'FormLibraryMirrored' } : undefined,
          styles: {
            text: {
              fontVariantLigatures: 'no-contextual',
            },
          },
        },
      },
    },
    {
      key: 'jobType',
      name: 'Type',
      fieldName: 'jobType',
      maxWidth: 200,
      minWidth: groupColumns ? 204 : 200,
    },
    {
      key: 'jobStatus',
      name: 'Status',
      fieldName: 'jobStatus',
      maxWidth: 140,
      minWidth: groupColumns ? 144 : 140,
      onRender: (item) =>
        onRenderTagColumn(item, 'Status', 'jobStatus', jobStatusColorMap.get(item.jobStatus), !isCardView),
    },
    {
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'featureSetName',
      minWidth: groupColumns ? 154 : 150,
      maxWidth: 150,
    },
    {
      key: 'createdBy',
      name: 'Started By',
      fieldName: 'creator',
      minWidth: groupColumns ? 124 : 120,
      maxWidth: 120,
    },
    {
      key: 'createdOn',
      name: 'Create On',
      fieldName: 'createdOn',
      minWidth: groupColumns ? 114 : 110,
      maxWidth: 180,
      data: {
        listCellProps: {
          onRenderText: (item: Job) => <>{formatDefaultDateAndTimeWithSeconds(item.createdOn)}</>,
        },
      },
    },
    {
      key: 'derived',
      name: 'Derived',
      fieldName: 'derived',
      minWidth: groupColumns ? 114 : 110,
      maxWidth: 110,
      onRender: (item) =>
        item.isDerived ? onRenderTagColumn(item, 'Derived', 'derived', defaultPalette.yellow100, true) : <></>,
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: groupColumns ? 317 : 345,
      onRender: (item: JobWithInfo) => {
        const { jobId = '', jobStatus, jobType, errorMessage } = item;
        const showCancelButton =
          jobStatus === JobStatus.Pending || jobStatus === JobStatus.PendingRerun || jobStatus === JobStatus.Running;
        const showDownloadButton = jobType === JobType.Retrieve && jobStatus === JobStatus.Success;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              {showDownloadButton && (
                <Link
                  styles={linkStylesGhost}
                  onClick={(e: React.MouseEvent) => {
                    onDownload(item);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Download
                </Link>
              )}
              <Link
                styles={linkStylesGhost}
                onClick={(e: React.MouseEvent) => {
                  onShowProgress(e.currentTarget as HTMLDivElement, jobId);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                Show Progress
              </Link>
              {errorMessage && (
                <Link
                  styles={linkStylesGhost}
                  onClick={(e: React.MouseEvent) => {
                    onShowError(e.currentTarget as HTMLDivElement, item);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Show Error
                </Link>
              )}
              {showCancelButton && (
                <Link styles={linkStylesGhost} onClick={() => onCancel(jobId)}>
                  Cancel
                </Link>
              )}
            </div>
          </div>
        );
      },
    },
  ];
  return groupColumns ? adjustColumnWidth(getRowWidthByColumns(groupColumns), columns, [1, 2, 3, 4, 5, 6, 7]) : columns;
};

interface IJobListProps {
  jobId?: string;
  featureSetId?: string;
  featureSetName?: string;
  jobStatus?: JobStatus;
  isCardView?: boolean;
  jobTypeFilters?: JobType[];
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
}

export const JobList = ({
  featureSetId,
  featureSetName,
  jobStatus,
  isCardView = true,
  jobTypeFilters,
  onUpdateData,
}: IJobListProps) => {
  const [groupsAndItems, setGroupsAndItems] = useState<GroupsAndItems>({
    groups: [],
    items: [],
  });
  const [progress, setProgress] = useState<JobProgressOutputProgress[]>([]);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [showCallout, setShowCallout] = useState(false);
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const refMeta = useRef<{
    isLoadingPage: boolean;
    calloutBtnEl?: HTMLElement;
    calloutType?: CalloutType;
    selectedJob?: JobWithInfo;
    mounted?: boolean;
  }>({
    isLoadingPage: false,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ groups, items, pageToken }: GroupsAndItems = { items: [], groups: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading jobs...`);
      try {
        const pageSize = defaultPageSize;
        let page: ListJobsPageResponse = {};
        if (featureSetId) {
          const req: ListJobsPageByFeatureSetIdRequest = {
            statusFilter: jobStatus,
            featureSetId,
            jobTypeFilters,
            pageSize,
            pageToken,
          };
          page = await fsApi(CoreService.ListJobsPageByFeatureSetId, req);
        } else {
          const req: ListJobsPageByFeatureSetNameRequest = {
            statusFilter: jobStatus,
            featureSetNameQuery: featureSetName,
            jobTypeFilters,
            pageSize,
            pageToken,
          };
          page = await fsApi(CoreService.ListJobsPageByFeatureSetName, req);
        }
        const { entries = [], nextPageToken = '' } = page;
        const groupItems = getChildCountAndFlatNestedItems(entries);
        const groupsOfPages = getGroupsWithEmptyGroupsOfNextPage(
          groups,
          groupItems,
          pageSize,
          convertListableJobToJobWithInfo,
          'jobId',
          'childCount'
        );
        groupItems.forEach((item) => {
          item.children.forEach((child) => items.push(convertListableJobToJobWithInfo(child)));
        });
        const newData = { groups: [...groupsOfPages], items: [...items], pageToken: nextPageToken };
        if (!refMeta.current.mounted) return;
        setGroupsAndItems(newData);
        onUpdateData && onUpdateData(newData.groups.length, pageSize, newData.pageToken, ContentsTitle.jobs);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading jobs: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, featureSetId, featureSetName, jobStatus, jobTypeFilters]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onCancel = useCallback(
    async (jobId?: string) => {
      setLoadingMessage(`Canceling the job(${jobId})`);
      try {
        await fsApi(CoreService.CancelJob, {
          jobId,
        });
        addToast({
          messageBarType: MessageBarType.success,
          message: `Job(${jobId}) has been canceled successfully!`,
        });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while canceling the job: `));
      } finally {
        setLoadingMessage('');
        load();
      }
    },
    [addToast, load]
  );
  const loadProgress = useCallback(
    async (jobId?: string) => {
      setLoadingProgress(true);
      try {
        const { progress = [] }: JobProgressOutput = await fsApi(CoreService.GetJobProgress, {
          jobId,
          nextIndex: 0,
        });
        setProgress(progress);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading the progress: `));
      } finally {
        setLoadingProgress(false);
      }
    },
    [addToast]
  );
  const onShowProgress = useCallback(
    (target: HTMLDivElement, jobId: string) => {
      refMeta.current.calloutBtnEl = target;
      refMeta.current.calloutType = CalloutType.Progress;
      setShowCallout(true);
      loadProgress(jobId);
    },
    [loadProgress]
  );

  const onShowError = useCallback((target: HTMLDivElement, job: JobWithInfo) => {
    if (job.errorMessage) {
      refMeta.current.calloutBtnEl = target;
      refMeta.current.selectedJob = job;
      refMeta.current.calloutType = CalloutType.Error;
      setShowCallout(true);
    }
  }, []);

  const onDownload = useCallback((job: JobWithInfo) => {
    refMeta.current.selectedJob = job;
    setShowDownloadPanel(true);
  }, []);
  const calloutProps: ICalloutProps = {
    target: refMeta.current.calloutBtnEl,
    styles: calloutContentStylesPopover,
    onDismiss: () => {
      setShowCallout(false);
    },
  };
  const renderCallout = useCallback(() => {
    if (refMeta.current.calloutType === CalloutType.Progress) {
      return loadingProgress ? (
        <Loader label="Loading progress..." />
      ) : (
        <div data-test="job-progress-callout">
          {progress && progress.length > 0
            ? progress.map(({ message, timestamp }, i) => <div key={i}>{`${timestamp}: ${message}`}</div>)
            : 'Data not present yet'}
        </div>
      );
    } else {
      return (
        <div style={{ whiteSpace: 'pre-line' }} data-test="job-error-callout">
          {refMeta.current.selectedJob?.errorMessage}
        </div>
      );
    }
  }, [loadingProgress, progress, showCallout]);
  const detailsListProps: IDetailsListProps = {
    styles: isCardView ? detailsListStylesGroupCard : detailsListStylesGroup,
    headerStyles: detailsHeaderStylesGroup,
    columns: getColumns(isCardView, onShowProgress, onShowError, onCancel, onDownload),
    isHeaderVisible: !isCardView,
    groups: groupsAndItems.groups,
    items: groupsAndItems.items,
    rowType: isCardView ? ListRowType.GroupHeaderCard : ListRowType.GroupHeader,
    getGroupHeight: isCardView
      ? (group: IGroup) => getGroupHeight(group)
      : (group: IGroup) => getGroupHeight(group, ListRowType.GroupHeader),
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    groupProps: {
      collapseAllVisibility: CollapseAllVisibility.hidden,
      showEmptyGroups: true,
      onRenderHeader: (props?: IDetailsGroupDividerProps) => {
        const { columns = [], group, onToggleCollapse } = props || {};
        const { data, isCollapsed, count } = group as IGroupWithDataType;
        if (!data.hasGroupHeaderData && !refMeta.current.isLoadingPage) {
          refMeta.current.isLoadingPage = true;
          onLoad(groupsAndItems, false);
        }
        return (
          <ListRow
            styles={isCardView ? undefined : listRowStylesThin}
            data={data.data}
            columns={columns}
            group={group}
            collapsed={isCollapsed}
            type={isCardView ? ListRowType.GroupHeaderCard : ListRowType.GroupHeader}
            hasOpenClose={count > 0}
            hasLeftOpenClose={!isCardView}
            hideHeader={!isCardView}
            loading={!data.hasGroupHeaderData}
            onOpen={() => onToggleCollapse!(group!)}
          />
        );
      },
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns } = props || {};
      return (
        <ListRow
          data={item}
          columns={getColumns(isCardView, onShowProgress, onShowError, onCancel, onDownload, columns)}
          type={ListRowType.Group}
        />
      );
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    load();
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load]);
  const classNames = useClassNames<IJobListStyles, IJobListClassNames>('JobList', jobListStylesDefault);
  return (
    <div className={classNames.root} data-test="job-list">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : isCardView ? (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="job-list-card-view">
          {!loadingMessage && groupsAndItems.groups.length === 0 ? (
            <EmptyMessage message="No Jobs Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      ) : (
        <div className={classNames.listView}>
          <div className={classNames.listViewWrap} data-is-scrollable="true" data-test="job-list-list-view">
            {!loadingMessage && groupsAndItems.groups.length === 0 ? (
              <EmptyMessage message="No Jobs Available" />
            ) : (
              <DetailsList {...detailsListProps} />
            )}
          </div>
        </div>
      )}
      {showCallout && (
        <Callout {...calloutProps}>
          <div className={classNames.calloutContents}>
            <div className={classNames.calloutHeader}>
              <span>{refMeta.current.calloutType}</span>
              <IconButton
                styles={iconButtonStylesNoBG}
                iconName="Clear"
                onClick={() => setShowCallout(false)}
                data-test="owner-list-close-button"
              />
            </div>
            <div className={classNames.calloutBody}>{renderCallout()}</div>
          </div>
        </Callout>
      )}
      <RetrieveDownloadPanel
        show={showDownloadPanel}
        job={refMeta.current.selectedJob}
        onDismiss={() => {
          setShowDownloadPanel(false);
        }}
      />
    </div>
  );
};
